import { api } from './api';

export interface SerialNumber {
  id: string;
  companyId: string;
  productId: string;
  serialNumber: string;
  warehouseId?: string;
  status: 'IN_STOCK' | 'SOLD' | 'DAMAGED' | 'RETURNED';
  condition: 'NEW' | 'USED' | 'REFURBISHED';
  purchaseDate?: string;
  purchaseBillId?: string;
  purchaseVendorId?: string;
  purchaseCost?: number;
  saleDate?: string;
  saleInvoiceId?: string;
  saleCustomerId?: string;
  salePrice?: number;
  warrantyExpiry?: string;
  notes?: string;
  location?: string;
  createdAt: string;
  updatedAt: string;
  product?: {
    id: string;
    name: string;
    sku: string;
    itemType: string;
  };
  warehouse?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface SerialNumbersResponse {
  success: boolean;
  data: SerialNumber[];
}

export interface TransferStockData {
  productId: string;
  fromWarehouseId: string;
  toWarehouseId: string;
  quantity: number;
  notes?: string;
}

export interface TransferStockResponse {
  success: boolean;
  message: string;
  data: {
    outMovement: any;
    inMovement: any;
  };
}

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  isActive: boolean;
  isPrimary: boolean;
}

export interface WarehouseStock {
  id: string;
  productId: string;
  warehouseId: string;
  quantityOnHand: number;
  quantityReserved: number;
  product: {
    id: string;
    name: string;
    sku: string;
    itemType: string;
    isSerialized: boolean;
  };
}

export interface WarehouseStockResponse {
  success: boolean;
  data: {
    warehouse: Warehouse;
    stock: WarehouseStock[];
  };
}

export interface WarehousesResponse {
  success: boolean;
  data: Warehouse[];
}

export interface CreateWarehouseData {
  name: string;
  code: string;
  address?: string;
  city?: string;
  state?: string;
}

export interface CreateWarehouseResponse {
  success: boolean;
  data: Warehouse;
}

const stockMovementService = {
  // Get all serial numbers for a product
  async getSerialNumbers(productId: string, status?: string, warehouseId?: string): Promise<SerialNumber[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (warehouseId) params.append('warehouseId', warehouseId);

    const response = await api.get<SerialNumbersResponse>(
      `/stock-movements/product/${productId}/serials?${params.toString()}`
    );
    return response.data.data;
  },

  // Get available serial numbers for sale
  async getAvailableSerialNumbers(productId: string, warehouseId?: string): Promise<SerialNumber[]> {
    const params = new URLSearchParams();
    if (warehouseId) params.append('warehouseId', warehouseId);

    const response = await api.get<SerialNumbersResponse>(
      `/stock-movements/product/${productId}/serials/available?${params.toString()}`
    );
    return response.data.data;
  },

  // Get all available machines for rental (SERIALIZED items with IN_STOCK status)
  async getAllAvailableMachines(): Promise<SerialNumber[]> {
    const response = await api.get<SerialNumbersResponse>(
      '/stock-movements/available-machines'
    );
    return response.data.data;
  },

  // Transfer stock between warehouses
  async transferStock(data: TransferStockData): Promise<TransferStockResponse> {
    const response = await api.post<TransferStockResponse>(
      '/stock-movements/transfer',
      data
    );
    return response.data;
  },

  // Get all warehouses
  async getWarehouses(): Promise<Warehouse[]> {
    const response = await api.get<WarehousesResponse>('/warehouses');
    return response.data.data;
  },

  // Get warehouse stock
  async getWarehouseStock(warehouseId: string, productType?: string): Promise<WarehouseStockResponse['data']> {
    const params = new URLSearchParams();
    if (productType) params.append('productType', productType);

    const response = await api.get<WarehouseStockResponse>(
      `/warehouses/${warehouseId}/stock?${params.toString()}`
    );
    return response.data.data;
  },

  // Create new warehouse
  async createWarehouse(data: CreateWarehouseData): Promise<Warehouse> {
    const response = await api.post<CreateWarehouseResponse>('/warehouses', data);
    return response.data.data;
  },
};

export default stockMovementService;
